import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'next-i18next';
import { Icon } from 'components';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import { useApp } from 'lib/contexts/save-in-app-context';
import AdditionalBadgeModal from '../AdditionalBadgeModal';
import { AdditionalBadgeModalType } from '../AdditionalBadgeModal/AdditionalBadgeModal.component';
import styles from './AdditionalBadge.module.scss';

export const AdditionalBadge = () => {
  const [isBadgeModalOpen, setBadgeModalOpen] = useState(false);

  const [modalToShow, setModalToShow] =
    useState<AdditionalBadgeModalType | null>(null);
  const [t] = useTranslation('candidate.profile');
  const toggleBadgeModal = () => {
    setBadgeModalOpen((open) => !open);
  };
  const {
    basicInfoComplete,
    educationComplete,
    experienceComplete,
    percentCompleted,
  } = useCandidateProfile();

  const { handleAutofillApplicationsClick } = useApp();

  const percentCompletedRef = useRef(percentCompleted);

  useEffect(() => {
    if (!basicInfoComplete || !educationComplete) {
      setModalToShow(AdditionalBadgeModalType.INVISIBLE_TO_RECRUITERS);
    } else if (basicInfoComplete && educationComplete && !experienceComplete) {
      setModalToShow(AdditionalBadgeModalType.AUTOFILL_APPLICATIONS);
    } else if (
      basicInfoComplete &&
      educationComplete &&
      experienceComplete &&
      percentCompleted !== 100
    ) {
      setModalToShow(AdditionalBadgeModalType.ALMOST_DONE);
    }
  }, [
    basicInfoComplete,
    educationComplete,
    experienceComplete,
    percentCompleted,
  ]);

  useEffect(() => {
    if (
      percentCompletedRef.current > 0 &&
      percentCompletedRef.current < 100 &&
      percentCompleted === 100
    ) {
      setModalToShow(AdditionalBadgeModalType.PROFILE_COMPLETED);
      setBadgeModalOpen(true);
    }
  }, [percentCompleted, percentCompletedRef]);

  useEffect(() => {
    percentCompletedRef.current = percentCompleted;
  }, [percentCompleted]);

  const onConfirm = () => {
    if (basicInfoComplete && educationComplete && !experienceComplete) {
      handleAutofillApplicationsClick();
    } else if (percentCompleted === 100) {
      handleAutofillApplicationsClick();
    } else {
      setBadgeModalOpen(false);
    }
  };

  return (
    <>
      <section className={styles.container}>
        {(!basicInfoComplete || !educationComplete) && (
          <section
            className={cn(styles.innerContainer, styles.invisibleToRecruiter)}
            onClick={toggleBadgeModal}
          >
            <Icon iconName="icon_hide" className={styles.icon} />
            <p className={styles.text}>
              {t('additional-badge.invisible-to-recruiters')}
            </p>
          </section>
        )}
        {basicInfoComplete && educationComplete && !experienceComplete && (
          <section
            className={cn(styles.innerContainer, styles.autofillApplications)}
            onClick={toggleBadgeModal}
          >
            <Icon iconName="icon_bolt" className={styles.icon} />
            <p className={styles.text}>
              {t('additional-badge.autofill-application')}
            </p>
          </section>
        )}
        {basicInfoComplete &&
          educationComplete &&
          experienceComplete &&
          percentCompleted !== 100 && (
            <section
              className={cn(styles.innerContainer, styles.almostDone)}
              onClick={toggleBadgeModal}
            >
              <Icon iconName="icon_progress-2" className={styles.icon} />
              <p className={styles.text}>{t('additional-badge.almost-done')}</p>
            </section>
          )}
      </section>
      {isBadgeModalOpen && (
        <AdditionalBadgeModal
          isOpen={isBadgeModalOpen}
          onClose={toggleBadgeModal}
          modalType={modalToShow}
          onConfirm={onConfirm}
        />
      )}
    </>
  );
};
